<template>
  <div class="bg-background-neutral-secondary space-y-3 rounded-2xl p-4">
    <div class="flex items-center space-x-3">
      <span
        class="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl"
      >
        <img
          v-if="
            !cityImageFailed && getCityImgSrc(arrivalPoint.airport, dict, true)
          "
          class="h-full w-full rounded-xl object-cover"
          height="42"
          width="42"
          :src="getCityImgSrc(arrivalPoint.airport, dict)"
          :alt="arrivalPoint.city"
          @error="cityImageFailed = true"
        />
        <PlaneIcon v-else class="h-10 w-10" />
      </span>
      <div>
        <div class="text-text-neutral-primary flex items-center space-x-1">
          <span class="font-semibold">{{ departurePoint.city }}</span>
          <OneArrowIcon
            v-if="numberOfFlights === 1"
            class="fill-text-neutral-primary"
          />
          <TwoArrowIcon
            v-else-if="!isMultiTrip"
            class="fill-text-neutral-primary"
          />
          <RouteIcon
            v-else-if="isMultiTrip"
            class="text-text-neutral-primary h-4 w-4 shrink-0"
          />
          <span class="font-semibold">{{ arrivalPoint.city }}</span>
        </div>
        <p class="text-text-neutral-quaternary text-sm">
          {{ dates }} • {{ countPassengers }}
          {{ t('general.shortPassengers') }} •
          {{ cabinClassText }}
        </p>
      </div>
    </div>
    <FreeButton
      size="sm"
      type="button"
      class="gray-button w-full"
      @click="emit('onCardClick')"
    >
      {{ t('details.flightDetails') }}
    </FreeButton>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';
  import { hashCodeLib, type ticketDetails } from 'shared';
  import { getCityImgSrc, FreeButton } from 'freedom-shared';
  import type { Dict } from '@aviata/platform-types';

  import { bookingStateModel } from '@/features/booking-state';

  import RouteIcon from '@/assets/images/multi-trip/route-icon-16x16.svg?component';

  import OneArrowIcon from './assets/one-arrow-icon.svg?component';
  import TwoArrowIcon from './assets/two-arrows-icon.svg?component';
  import PlaneIcon from './assets/plane-green-outline.svg?component';

  const props = defineProps<{
    flights: ticketDetails.types.MappedFlight[];
    dict?: Dict.Entity;
    sourcePage?: string;
    countPassengers: number;
    isYouth: boolean;
    /** When set (e.g. payment from order), avoids relying on booking store offer. */
    searchQuery?: string | null;
  }>();

  const emit = defineEmits<{
    onCardClick: [];
  }>();

  const { t } = useI18n();

  const cityImageFailed = ref(false);

  const bookingStore = bookingStateModel.useBookingStore();
  const { offer } = storeToRefs(bookingStore);

  const firstFlight = computed(() => props.flights[0]);
  const lastFlight = computed(() => props.flights.slice(-1)[0]);
  const numberOfFlights = computed(() => props.flights.length);
  const isMultiTrip = computed(() => {
    const query = props.searchQuery ?? offer.value?.$meta?.search_query;
    return typeof query === 'string' && hashCodeLib.isMultiTripSearch(query);
  });

  const dates = computed(() => {
    if (numberOfFlights.value === 1) {
      return firstFlight.value.segments[0].dep.formattedDate;
    }
    return props.flights
      .map(flight => flight.segments[0].dep.formattedDate)
      .join(' - ');
  });
  const cabinClassText = computed(() => {
    const cabinClass = firstFlight.value.segments[0].cabin.class;
    if (cabinClass === 'Economy' && !props.isYouth) {
      return t('general.cabinClass.economy');
    }
    if (cabinClass === 'Business') {
      return t('general.cabinClass.business');
    }
    return t('general.cabinClass.youth');
  });

  // For round-trip (2 flights) the trip destination is the first flight's arrival
  // (the user returns home on the second flight). For multi-city (search hash) we
  // surface the final leg's arrival so the header shows the overall journey.
  const departurePoint = computed(() => firstFlight.value.segments[0].dep);
  const arrivalPoint = computed(() =>
    isMultiTrip.value
      ? lastFlight.value.segments.slice(-1)[0].arr
      : firstFlight.value.segments.slice(-1)[0].arr,
  );
</script>
